import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import sanityClient from '../lib/sanityClient';
import { Calendar, ChevronRight, MapPin, Clock } from 'lucide-react';
import { Card } from './ui/Card';

function HeroSection() {
  const [nextGame, setNextGame] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const checkIfMobile = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
      const isPortrait = window.innerHeight > window.innerWidth;
      const isSmallScreen = window.innerWidth < 768;
      setIsMobile((isMobileDevice && isPortrait) || (isSmallScreen && isPortrait));
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    window.addEventListener('orientationchange', checkIfMobile);

    return () => {
      window.removeEventListener('resize', checkIfMobile);
      window.removeEventListener('orientationchange', checkIfMobile);
    };
  }, []);

  useEffect(() => {
    const fetchNextGame = async () => {
      try {
        const today = new Date();
        // Set time to start of day for proper comparison
        today.setHours(0, 0, 0, 0);
        const formattedDate = today.toISOString();
        const game = await sanityClient.fetch(
          `*[_type == "event" && eventType == "game" && startDate >= $today] | order(startDate asc)[0]{
            _id,
            title,
            startDate,
            opponent,
            location,
            isHomeGame
          }`,
          { today: formattedDate }
        );
        setNextGame(game || null);
      } catch (error) {
        console.error('Error fetching next game:', error);
        setNextGame(null);
      }
    };

    fetchNextGame();
  }, []);

  const formatGameDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatGameTime = (dateString) => {
    if (!dateString) return '';
    // console.log('Raw date from Sanity:', dateString);
    // Sanity returns ISO 8601 format, so we can use it directly
    const date = new Date(dateString);
    // console.log('Parsed date:', date);
    if (isNaN(date.getTime())) {
      console.error('Invalid date:', dateString);
      return 'Time TBD';
    }
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Los_Angeles',
    });
  };

  const getNextGameText = () => {
    if (!nextGame) return 'No upcoming games scheduled';

    const gameDate = formatGameDate(nextGame.startDate);
    const gameTime = formatGameTime(nextGame.startDate);

    return `${nextGame.isHomeGame ? 'vs' : '@'} ${nextGame.opponent} - ${gameDate} ${gameTime}`;
  };

  const handleVideoLoaded = () => {
    setVideoLoaded(true);
    if (videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.error('Video playback error:', error);
        setVideoError(true);
      });
    }
  };

  return (
    <section className="relative">
      {/* Video Background */}
      <div className="relative h-[600px] md:h-[700px] overflow-hidden">
        {!videoError && (
          <>
            <video
              ref={videoRef}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                videoLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              loop
              muted
              playsInline
              preload="auto"
              onCanPlay={handleVideoLoaded}
              poster="/hero-poster.jpg"
            >
              <source src="/bomb-video.webm" type="video/webm" />
            </video>
            {/* Enhanced gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black md:from-black/70 md:via-black/50 md:to-black" />
          </>
        )}
        {videoError && (
          <div className="absolute inset-0 w-full h-full">
            <img
              src="/hero-poster.jpg"
              alt="Bombardiers Hockey"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black md:from-black/70 md:via-black/50 md:to-black" />
          </div>
        )}

        {/* Hero Content */}
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 drop-shadow-lg">
              Welcome to
              <br />
              <span className="text-yellow-500">Bombardiers Hockey</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 drop-shadow">
              Join us for another exciting season of hockey in Ontario, California. Follow our
              journey, watch our games, and be part of our community.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/schedule"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-black bg-yellow-500 hover:bg-yellow-400 transition-colors shadow-lg"
              >
                View Schedule
              </Link>
              <Link
                to="/team"
                className="inline-flex items-center px-6 py-3 border border-yellow-500 text-base font-medium rounded-md text-yellow-500 hover:bg-yellow-500 hover:text-black transition-colors shadow-lg backdrop-blur-sm"
              >
                Meet the Team
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Next Game Section */}
      {isMobile ? (
        <div className="bg-gray-900 px-4 py-6">
          <Card className="bg-black border border-gray-800">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <Calendar className="h-5 w-5 text-yellow-500 mr-2" />
                <h2 className="text-lg font-bold text-yellow-500">NEXT GAME</h2>
              </div>

              {nextGame ? (
                <>
                  <div className="mb-6">
                    <div className="text-xl font-bold text-white mb-2">
                      {nextGame.isHomeGame ? (
                        <>
                          <span className="text-yellow-500">BOMBARDIERS</span>
                          <span className="text-gray-400 mx-2">vs</span>
                          <span>{nextGame.opponent}</span>
                        </>
                      ) : (
                        <>
                          <span>{nextGame.opponent}</span>
                          <span className="text-gray-400 mx-2">vs</span>
                          <span className="text-yellow-500">BOMBARDIERS</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center text-gray-400 mb-2">
                      <Calendar className="h-4 w-4 mr-2" />
                      <span>{formatGameDate(nextGame.startDate)}</span>
                    </div>
                    <div className="flex items-center text-gray-400 mb-2">
                      <Clock className="h-4 w-4 mr-2" />
                      <span>{formatGameTime(nextGame.startDate)}</span>
                    </div>
                    {nextGame.location && (
                      <div className="flex items-center text-gray-400">
                        <MapPin className="h-4 w-4 mr-2" />
                        <span>{nextGame.location}</span>
                      </div>
                    )}
                  </div>
                  <Link
                    to={`/games/${nextGame._id}`}
                    className="inline-flex items-center text-yellow-500 hover:text-yellow-400"
                  >
                    View Game Details
                    <ChevronRight className="ml-1 h-5 w-5" />
                  </Link>
                </>
              ) : (
                <p className="text-gray-400">No upcoming games scheduled</p>
              )}
            </div>
          </Card>
        </div>
      ) : (
        <div className="bg-gray-900/95 backdrop-blur-sm py-4 border-t border-b border-gray-800/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center">
                <span className="text-yellow-500 font-bold mr-3 flex items-center">
                  <Calendar className="h-5 w-5 mr-2" /> NEXT GAME
                </span>
                <span className="text-white text-lg">{getNextGameText()}</span>
              </div>
              {nextGame && (
                <Link
                  to={`/games/${nextGame._id}`}
                  className="ml-4 text-yellow-400 hover:text-yellow-300 transition-colors flex items-center group"
                >
                  View Details
                  <ChevronRight className="ml-1 h-5 w-5 transform group-hover:translate-x-1 transition-transform" />
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default HeroSection;
